import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { eventFormTone, type EventFormTone } from "./eventFormTone";

const availableTimes = (minTime: string, maxTime: string) =>
    Array.from({ length: 24 * 4 }, (_, i) => {
        const hours = Math.floor(i / 4)
            .toString()
            .padStart(2, "0");
        const minutes = ((i % 4) * 15).toString().padStart(2, "0");

        return `${hours}:${minutes}`;
    }).filter((time) => time >= minTime && time <= maxTime);

type EventTimePickerProps = {
    time?: string;
    setTime: (time: string) => void;
    minTime?: string;
    maxTime?: string;
    tone?: EventFormTone;
};

const EventTimePicker = ({
    time,
    setTime,
    minTime = "00:00",
    maxTime = "23:45",
    tone = "dark",
}: EventTimePickerProps) => {
    const { t } = useTranslation();
    const value = (
        <SelectValue
            placeholder={t("dashboard.widgets.calendar.event.selectTime")}
        />
    );

    return (
        <div className="flex flex-col gap-1">
            <Select onValueChange={setTime} value={time}>
                <SelectTrigger
                    className={cn(
                        "border",
                        eventFormTone[tone].field,
                        eventFormTone[tone].border,
                        tone === "light" &&
                            "gap-3 [&>svg:last-child]:size-5 [&>svg:last-child]:opacity-100",
                    )}
                >
                    {tone === "light" && (
                        <Clock
                            aria-hidden
                            className="size-5 flex-none"
                            strokeWidth={1.5}
                        />
                    )}
                    {tone === "light" ? (
                        <span className="flex-1 text-left">{value}</span>
                    ) : (
                        value
                    )}
                </SelectTrigger>
                <SelectContent>
                    {availableTimes(minTime, maxTime).map((availableTime) => (
                        <SelectItem key={availableTime} value={availableTime}>
                            {availableTime}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
};

export default EventTimePicker;
